import type { MaybeRefOrGetter } from 'vue';
import { toValue } from 'vue';

import { formatMutationError, type MutationErrorOverride } from '@/modules/campaigns/utils/mutation-errors/shared';

import type { ErrorController } from './useErrorController';

export type MutationErrorHandlerSpec = Readonly<{
  errors: ErrorController;
  title: MaybeRefOrGetter<string>;
  override: MutationErrorOverride;
  genericMessage: MaybeRefOrGetter<string>;
  /** Stable English diagnostic identifier; never pass translated display copy. */
  logTag: string;
}>;

/**
 * Creates an `(error: unknown) => void` handler that:
 *   1. logs the raw error to `console.error` (single source of truth — callsites
 *      should not log themselves);
 *   2. pushes a formatted entry into the linked `ErrorPanel`.
 *
 * Pair with a TanStack `onError` callback or a manual `try/catch` to keep
 * callsites declarative.
 */
export const createMutationErrorHandler =
  (spec: MutationErrorHandlerSpec) =>
  (error: unknown): void => {
    // eslint-disable-next-line no-console
    console.error(`${spec.logTag} failed:`, error);
    spec.errors.showErrorItem({
      title: toValue(spec.title),
      description: formatMutationError(error, spec.override, toValue(spec.genericMessage)),
    });
  };
